import { Controller } from "@hotwired/stimulus"
import pageMessageBroker from "../page_message_broker"

// Connects to data-controller="plan-design-selection-modal"
export default class extends Controller {
  static targets = ["modal", "searchInput", "planDesignsList", "selectionSummary", "addButton", "clientFilterCheckbox", "clientFilterLabel"]
  static values = {
    workspaceId: String,
    clientName: String,
  }

  connect() {
    this.currentPage = 1
    this.currentSearch = ""
    this.currentExcludeIds = ""
    this.currentClientId = ""
    this.currentRtqId = ""
    this.currentMarketingId = ""
    this.filterByClient = true
    this.mode = "comparison" // "comparison" or "rtq"
    this.selectedPlanDesignIds = new Set()

    this.subscription = pageMessageBroker.subscribe("open-plan-design-selection-modal", this.handleModalOpen.bind(this))
    this.rtqSubscription = pageMessageBroker.subscribe("open-plan-design-modal", this.handleRtqModalOpen.bind(this))
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.rtqSubscription) {
      this.rtqSubscription.unsubscribe()
    }
  }

  handleModalOpen(event) {
    this.mode = "comparison"
    this.currentExcludeIds = event.detail.currentIds || ""
    this.currentClientId = event.detail.clientId || ""
    this.open()
  }

  handleRtqModalOpen(event) {
    this.mode = "rtq"
    this.currentRtqId = event.detail.rtqId || ""
    this.currentMarketingId = event.detail.marketingId || ""
    this.currentClientId = event.detail.clientId || ""
    this.currentExcludeIds = event.detail.excludeIds || ""
    this.filterByClient = true
    this.open()
  }

  open() {
    this.showModal()
    this.updateClientFilterUI()
    this.updateButtonText()
    this.loadPlanDesigns()
  }

  close() {
    this.hideModal()
    this.resetModal()
  }

  search(event) {
    event.preventDefault()
    this.currentSearch = this.searchInputTarget.value.trim()
    this.currentPage = 1
    this.loadPlanDesigns()
  }

  toggleClientFilter(event) {
    this.filterByClient = event.target.checked
    this.currentPage = 1
    this.loadPlanDesigns()
  }

  updateClientFilterUI() {
    if (this.hasClientFilterCheckboxTarget && this.clientNameValue && this.currentClientId) {
      this.clientFilterCheckboxTarget.checked = this.filterByClient
      this.clientFilterLabelTarget.textContent = `Show only ${this.clientNameValue} plan designs`
      this.clientFilterCheckboxTarget.closest("div").classList.remove("hidden")
    } else if (this.hasClientFilterCheckboxTarget) {
      this.clientFilterCheckboxTarget.closest("div").classList.add("hidden")
    }
  }

  updateButtonText() {
    if (this.mode === "rtq") {
      this.addButtonTarget.textContent = "Add Selected Plan Designs"
    } else {
      this.addButtonTarget.textContent = "Add to Comparison"
    }
  }

  goToPage(event) {
    event.preventDefault()
    this.currentPage = parseInt(event.target.dataset.page)
    this.loadPlanDesigns()
  }

  togglePlanDesign(event) {
    const planDesignId = event.target.dataset.planDesignId
    if (event.target.checked) {
      this.selectedPlanDesignIds.add(planDesignId)
    } else {
      this.selectedPlanDesignIds.delete(planDesignId)
    }
    this.updateSelectionSummary()
  }

  updateSelectionSummary() {
    const count = this.selectedPlanDesignIds.size

    if (count === 0) {
      this.selectionSummaryTarget.textContent = ""
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    } else {
      this.selectionSummaryTarget.textContent = `${count} plan design${count === 1 ? "" : "s"} selected`
      this.addButtonTarget.disabled = false
      this.addButtonTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
  }

  addSelected() {
    if (this.mode === "rtq") {
      this.addToRtq()
    } else {
      this.addToComparison()
    }
  }

  addToComparison() {
    if (this.selectedPlanDesignIds.size === 0) return

    const currentIds = this.currentExcludeIds ? this.currentExcludeIds.split(",").filter(Boolean) : []
    const newIds = [...currentIds, ...this.selectedPlanDesignIds]

    const url = new URL(window.location.href)
    url.searchParams.set("plan_design_ids", newIds.join(","))

    this.close()
    window.location.href = url.toString()
  }

  async addToRtq() {
    if (this.selectedPlanDesignIds.size === 0) return

    this.addButtonTarget.disabled = true
    this.addButtonTarget.textContent = "Adding..."

    try {
      const response = await fetch(`/workspaces/${this.workspaceIdValue}/marketings/${this.currentMarketingId}/rtqs/${this.currentRtqId}/add_plan_designs`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').getAttribute("content")
        },
        body: JSON.stringify({
          plan_design_ids: [...this.selectedPlanDesignIds]
        })
      })

      if (response.ok) {
        this.close()
        window.location.reload()
      } else {
        const errorData = await response.json()
        alert(errorData.error || "Failed to add plan designs")
      }
    } catch (error) {
      console.error("Error adding to RTQ:", error)
      alert("An error occurred while adding plan designs")
    }

    this.addButtonTarget.disabled = false
    this.updateButtonText()
  }

  async loadPlanDesigns() {
    try {
      const params = new URLSearchParams({
        page: this.currentPage,
        search: this.currentSearch,
        exclude_ids: this.currentExcludeIds,
      })

      if (this.filterByClient && this.currentClientId) {
        params.set("client_id", this.currentClientId)
      }

      const response = await fetch(`/workspaces/${this.workspaceIdValue}/plan_designs.json?${params}`)
      const data = await response.json()
      this.renderPlanDesigns(data)
    } catch (error) {
      console.error("Error loading plan designs:", error)
      this.planDesignsListTarget.innerHTML = "<p class=\"text-red-600 p-4\">Error loading plan designs</p>"
    }
  }

  renderPlanDesigns(data) {
    if (!data.plan_designs || data.plan_designs.length === 0) {
      this.planDesignsListTarget.innerHTML = `
        <div class="p-4 text-center text-gray-500">
          ${this.currentSearch ? `No plan designs found for "${this.currentSearch}"` : "No plan designs available"}
        </div>
      `
      return
    }

    let html = "<div class=\"divide-y divide-gray-200\">"

    data.plan_designs.forEach(planDesign => {
      const isSelected = this.selectedPlanDesignIds.has(planDesign.id.toString())
      const name = planDesign.name || planDesign.id
      const clientName = planDesign.client_name || "No client"
      const createdAt = new Date(planDesign.created_at).toLocaleDateString()

      html += `
        <div class="p-4 hover:bg-gray-50">
          <div class="flex items-start space-x-3">
            <input type="checkbox"
                   ${isSelected ? "checked" : ""}
                   data-plan-design-id="${planDesign.id}"
                   data-action="change->plan-design-selection-modal#togglePlanDesign"
                   class="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50">
            <div class="flex-1">
              <h4 class="font-medium text-gray-900">${name}</h4>
              <p class="text-sm text-gray-500">
                Client: ${clientName} &bull; Created: ${createdAt}
              </p>
            </div>
          </div>
        </div>
      `
    })

    html += "</div>"

    if (data.total_pages > 1) {
      html += this.renderPagination(data)
    }

    this.planDesignsListTarget.innerHTML = html
  }

  renderPagination(data) {
    let html = `
      <div class="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Showing ${((data.current_page - 1) * data.per_page) + 1} to ${Math.min(data.current_page * data.per_page, data.total_count)} of ${data.total_count} results
          </div>
          <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
    `

    if (data.current_page > 1) {
      html += `
        <button data-page="${data.current_page - 1}"
                data-action="click->plan-design-selection-modal#goToPage"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
          <span class="sr-only">Previous</span>
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
          </svg>
        </button>
      `
    }

    for (let i = Math.max(1, data.current_page - 2); i <= Math.min(data.total_pages, data.current_page + 2); i++) {
      const activeClass = i === data.current_page ? "bg-blue-50 text-blue-600 border-blue-300" : "bg-white text-gray-700 hover:bg-gray-50"
      html += `
        <button data-page="${i}"
                data-action="click->plan-design-selection-modal#goToPage"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${activeClass}">
          ${i}
        </button>
      `
    }

    if (data.current_page < data.total_pages) {
      html += `
        <button data-page="${data.current_page + 1}"
                data-action="click->plan-design-selection-modal#goToPage"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50">
          <span class="sr-only">Next</span>
          <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
          </svg>
        </button>
      `
    }

    html += "</nav></div></div>"
    return html
  }

  showModal() {
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
  }

  hideModal() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  resetModal() {
    this.currentPage = 1
    this.currentSearch = ""
    this.filterByClient = true
    this.selectedPlanDesignIds.clear()
    this.searchInputTarget.value = ""
    this.selectionSummaryTarget.textContent = ""
    this.addButtonTarget.disabled = true
    this.addButtonTarget.classList.add("opacity-50", "cursor-not-allowed")
    this.planDesignsListTarget.innerHTML = ""
    if (this.hasClientFilterCheckboxTarget) {
      this.clientFilterCheckboxTarget.checked = true
    }
  }
}
